import i18n from "../../../i18n";
import { Trans } from "react-i18next";
import { useState, useMemo, useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Textarea } from "../../../components/ui/textarea";
import { cn } from "../../../lib/utils";
import { fetchPollDepartments, fetchPollManagers } from "../../../services/pollServices";
import {
  RESPONSE_TYPES,
  AUDIENCE_GROUPS,
  MIN_OPTIONS,
  MAX_OPTIONS,
  TIME_OPTIONS,
  DEFAULT_END_TIME,
  todayISO,
  toDateInputValue,
  toTimeInputValue,
} from "../constants/pollOptions";

export const fieldLabel = "block text-xs font-semibold text-[var(--color-text-secondary)] mb-1.5";
export const inputBase =
"w-full h-10 px-3 rounded-xl border bg-white text-sm outline-none focus:ring-1 focus:ring-[var(--color-accent)]";
export const okBorder = "border-[var(--color-text-border)]";
export const errBorder = "border-[var(--color-error)]";

function blankOptions() {
  return [
  { optionId: "o1", text: "" },
  { optionId: "o2", text: "" }];

}

/**
 * Form state + validation for Create Poll and the (draft-only) edit path.
 *
 * <p>There is no scheduling — start is always today and read-only — and
 * results visibility is two booleans rather than a mode.
 */
export function usePollForm(initial) {
  const [form, setForm] = useState(() => ({
    pollTitle: initial?.pollTitle ?? "",
    pollQuestion: initial?.pollQuestion ?? "",
    description: initial?.description ?? "",
    responseType: initial?.responseType ?? "",
    options: initial?.options?.length ? initial.options.map((o) => ({ ...o })) : blankOptions(),
    anonymous: initial?.anonymous ?? false,
    audience: initial?.audience ?? null,
    endDate: initial?.endDateTime ? toDateInputValue(initial.endDateTime) : "",
    endTime: initial?.endDateTime ? toTimeInputValue(initial.endDateTime) : DEFAULT_END_TIME,
    shareResults: initial?.shareResults ?? false,
    resultsAfterVoting: initial?.resultsAfterVoting ?? false
  }));
  const [touched, setTouched] = useState(false);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const needsOptions = useMemo(
    () => RESPONSE_TYPES.find((t) => t.key === form.responseType)?.needsOptions ?? false,
    [form.responseType]
  );

  const filledOptions = form.options.filter((o) => o.text.trim());
  const group = form.audience?.group ?? "";
  const needsQualifier = group === "Reportees" || group === "Department";

  const errors = {
    pollTitle: !form.pollTitle.trim() && "Poll title is required.",
    pollQuestion: !form.pollQuestion.trim() && "Poll question is required.",
    responseType: !form.responseType && "Select a response type.",
    options:
    needsOptions && filledOptions.length < MIN_OPTIONS ?
    `Add at least ${MIN_OPTIONS} options.` :
    needsOptions && filledOptions.length > MAX_OPTIONS ?
    `A poll can have at most ${MAX_OPTIONS} options.` :
    false,
    audience: !group ?
    "Select a recipient group." :
    group === "Reportees" && !form.audience.managerId ?
    "Enter the manager's ID for Reportees." :
    group === "Department" && !form.audience.departmentId ?
    "Enter the department ID." :
    false,
    endDate: !form.endDate ?
    "End date is required." :
    !form.endTime ?
    "End time is required." :
    new Date(`${form.endDate}T${form.endTime}`) <= new Date() ?
    "End date & time must be in the future." :
    false
  };

  const isValid = Object.values(errors).every((e) => !e);

  /** Form state → API payload shape. */
  const toPayload = () => ({
    ...form,
    options: needsOptions ? filledOptions : []
  });

  return { form, set, errors, isValid, needsOptions, needsQualifier, touched, setTouched, toPayload };
}

/** Small labelled on/off slider (used for Anonymous Voting). */
function Toggle({ checked, onChange, id }) {
  return (
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={cn(
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
        checked ? "bg-[var(--color-accent)]" : "bg-[var(--color-text-border)]"
      )}>
      
      <span
        className={cn(
          "inline-block h-5 w-5 rounded-full bg-white shadow transition-transform",
          checked ? "translate-x-[22px]" : "translate-x-0.5"
        )} />
      
    </button>);

}

/**
 * The Create Poll fields. Reused by the draft edit path.
 * Response type and recipients are dropdowns; anonymity is a slider; the start
 * date is fixed to today; the end is a date; visibility is two checkboxes.
 */
export function PollFormFields({ form, set, errors, needsOptions, touched }) {
  const [departments, setDepartments] = useState([]);
  const [managers, setManagers] = useState([]);
  const show = (key) => touched && errors[key];
  const group = form.audience?.group ?? "";

  // Load the qualifier lists only when the group that needs them is chosen.
  useEffect(() => {
    if (group !== "Department") return;
    let cancelled = false;
    fetchPollDepartments().then((d) => !cancelled && setDepartments(d)).catch(() => {});
    return () => {cancelled = true;};
  }, [group]);

  useEffect(() => {
    if (group !== "Reportees") return;
    let cancelled = false;
    fetchPollManagers().then((m) => !cancelled && setManagers(m)).catch(() => {});
    return () => {cancelled = true;};
  }, [group]);

  const setGroup = (g) =>
  set({ audience: { group: g, managerId: null, departmentId: null } });
  const setQualifier = (patch) =>
  set({ audience: { ...(form.audience ?? { group }), ...patch } });

  const setOption = (index, text) => {
    const options = form.options.map((o, i) => i === index ? { ...o, text } : o);
    set({ options });
  };
  const addOption = () => {
    if (form.options.length >= MAX_OPTIONS) return;
    set({ options: [...form.options, { optionId: `o${Date.now()}`, text: "" }] });
  };
  const removeOption = (index) => {
    if (form.options.length <= MIN_OPTIONS) return;
    set({ options: form.options.filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-5">
      {/* Poll Title */}
      <div>
        <label className={fieldLabel}><Trans i18nKey="polls.pollTitle" /> <span className="text-[var(--color-error)]">*</span></label>
        <input
          type="text"
          placeholder={i18n.t("examples.eGDiwaliShiftRosterPreference")}
          value={form.pollTitle}
          onChange={(e) => set({ pollTitle: e.target.value })}
          className={cn(inputBase, show("pollTitle") ? errBorder : okBorder)} />
        
        {show("pollTitle") && <p className="text-[11px] text-[var(--color-error)] mt-1">{errors.pollTitle}</p>}
      </div>

      {/* Poll Question */}
      <div>
        <label className={fieldLabel}><Trans i18nKey="polls.pollQuestion" /> <span className="text-[var(--color-error)]">*</span></label>
        <input
          type="text"
          placeholder={i18n.t("polls.theQuestionVotersAnswer")}
          value={form.pollQuestion}
          onChange={(e) => set({ pollQuestion: e.target.value })}
          className={cn(inputBase, show("pollQuestion") ? errBorder : okBorder)} />
        
        {show("pollQuestion") && <p className="text-[11px] text-[var(--color-error)] mt-1">{errors.pollQuestion}</p>}
      </div>

      {/* Description */}
      <div>
        <label className={fieldLabel}><Trans i18nKey="common.description2" /></label>
        <Textarea
          className="rounded-xl min-h-[80px] border-[var(--color-text-border)]"
          placeholder={i18n.t("polls.optionalContextForVoters")}
          maxLength={1000}
          value={form.description}
          onChange={(e) => set({ description: e.target.value })} />
        
      </div>

      {/* Response Type — dropdown */}
      <div>
        <label className={fieldLabel}><Trans i18nKey="polls.responseType" /> <span className="text-[var(--color-error)]">*</span></label>
        <Select
          value={form.responseType || undefined}
          onValueChange={(val) => {
            const needs = RESPONSE_TYPES.find((t) => t.key === val)?.needsOptions;
            set({
              responseType: val,
              options: needs ? form.options.length ? form.options : blankOptions() : form.options
            });
          }}>
          
          <SelectTrigger className={cn("h-10 rounded-xl bg-white", show("responseType") ? errBorder : okBorder)}>
            <SelectValue placeholder={i18n.t("polls.selectAResponseType")} />
          </SelectTrigger>
          <SelectContent>
            {RESPONSE_TYPES.map((t) =>
            <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
            )}
          </SelectContent>
        </Select>
        {show("responseType") && <p className="text-[11px] text-[var(--color-error)] mt-1">{errors.responseType}</p>}
      </div>

      {/* Poll Options — Single/Multiple Choice only */}
      {needsOptions &&
      <div>
          <label className={fieldLabel}>
            <Trans i18nKey="polls.pollOptions" /> <span className="text-[var(--color-error)]">*</span>
            <span className="ml-1 font-normal text-[var(--color-text-secondary)]">
              <Trans i18nKey="polls.min" /> {MIN_OPTIONS}<Trans i18nKey="polls.max" /> {MAX_OPTIONS})
            </span>
          </label>
          <div className="space-y-2">
            {form.options.map((option, index) =>
          <div key={option.optionId} className="flex items-center gap-2">
                <span className="grid place-items-center h-10 w-8 shrink-0 text-[12px] font-semibold text-[var(--color-text-secondary)]">
                  {index + 1}
                </span>
                <input
              type="text"
              placeholder={`Option ${index + 1}`}
              value={option.text}
              onChange={(e) => setOption(index, e.target.value)}
              className={cn(inputBase, show("options") ? errBorder : okBorder)} />
            
                <button
              type="button"
              onClick={() => removeOption(index)}
              disabled={form.options.length <= MIN_OPTIONS}
              aria-label={`Remove option ${index + 1}`}
              className="h-10 w-10 shrink-0 grid place-items-center rounded-xl border border-[var(--color-text-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-error)] hover:border-[var(--color-error)]/40 disabled:opacity-40 disabled:cursor-not-allowed">
              
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
          )}
          </div>
          <button
          type="button"
          onClick={addOption}
          disabled={form.options.length >= MAX_OPTIONS}
          className="mt-2 inline-flex items-center gap-1.5 h-10 px-4 rounded-xl border border-[var(--color-text-border)] bg-white text-sm font-semibold text-[var(--color-accent)] hover:bg-[var(--color-workspace)] disabled:opacity-50 disabled:cursor-not-allowed">
          
            <Plus className="h-4 w-4" />
            {form.options.length >= MAX_OPTIONS ? `Maximum ${MAX_OPTIONS} options` : "Add option"}
          </button>
          {show("options") && <p className="text-[11px] text-[var(--color-error)] mt-1">{errors.options}</p>}
        </div>
      }

      {/* Recipients — dropdown */}
      <div>
        <label className={fieldLabel}><Trans i18nKey="common.recipients" /> <span className="text-[var(--color-error)]">*</span></label>
        <Select value={group || undefined} onValueChange={setGroup}>
          <SelectTrigger className={cn("h-10 rounded-xl bg-white", show("audience") ? errBorder : okBorder)}>
            <SelectValue placeholder={i18n.t("polls.selectAudience")} />
          </SelectTrigger>
          <SelectContent>
            {AUDIENCE_GROUPS.map((g) =>
            <SelectItem key={g.key} value={g.key}>{g.label}</SelectItem>
            )}
          </SelectContent>
        </Select>

        {group === "Reportees" &&
        <div className="mt-2">
            <Select
            value={form.audience?.managerId ? String(form.audience.managerId) : undefined}
            onValueChange={(val) => setQualifier({ managerId: Number(val) })}>
            
              <SelectTrigger className={cn("h-10 rounded-xl bg-white", show("audience") ? errBorder : okBorder)}>
                <SelectValue placeholder={i18n.t("polls.selectManager")} />
              </SelectTrigger>
              <SelectContent>
                {managers.map((m) =>
              <SelectItem key={m.managerId} value={String(m.managerId)}>{m.managerName}</SelectItem>
              )}
              </SelectContent>
            </Select>
          </div>
        }
        {group === "Department" &&
        <div className="mt-2">
            <Select
            value={form.audience?.departmentId ? String(form.audience.departmentId) : undefined}
            onValueChange={(val) => setQualifier({ departmentId: Number(val) })}>
            
              <SelectTrigger className={cn("h-10 rounded-xl bg-white", show("audience") ? errBorder : okBorder)}>
                <SelectValue placeholder={i18n.t("polls.selectDepartment")} />
              </SelectTrigger>
              <SelectContent>
                {departments.map((d) =>
              <SelectItem key={d.departmentId} value={String(d.departmentId)}>{d.departmentName}</SelectItem>
              )}
              </SelectContent>
            </Select>
          </div>
        }
        {show("audience") && <p className="text-[11px] text-[var(--color-error)] mt-1">{errors.audience}</p>}
      </div>

      {/* Anonymous Voting — slider. Only the switch toggles it; the label text
           is not clickable (a htmlFor label would forward text clicks). */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <span className={cn(fieldLabel, "mb-0")}><Trans i18nKey="polls.anonymousVoting" /></span>
          <span className="block font-normal text-[11px] text-[var(--color-text-secondary)] mt-0.5">
            <Trans i18nKey="polls.hidesRespondentIdentityFromHrAndTheCreator" />
          </span>
        </div>
        <Toggle checked={form.anonymous} onChange={(v) => set({ anonymous: v })} />
      </div>

      {/* Dates — start read-only today; end is a date + a time dropdown */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={fieldLabel}><Trans i18nKey="dateTime.startDate" /></label>
          <input
            type="date"
            value={todayISO()}
            readOnly
            disabled
            className={cn(inputBase, okBorder, "bg-[var(--color-workspace)]/50 text-[var(--color-text-secondary)] cursor-not-allowed")} />
          
          <p className="text-[11px] text-[var(--color-text-secondary)] mt-1"><Trans i18nKey="dateTime.pollsOpenTodayWhenPublished" /></p>
        </div>
        <div>
          <label className={fieldLabel}><Trans i18nKey="dateTime.endDateTime" /> <span className="text-[var(--color-error)]">*</span></label>
          <div className="flex gap-2">
            <input
              type="date"
              min={todayISO()}
              value={form.endDate}
              onChange={(e) => set({ endDate: e.target.value })}
              className={cn(inputBase, "flex-1", show("endDate") ? errBorder : okBorder)} />
            
            <Select value={form.endTime || undefined} onValueChange={(val) => set({ endTime: val })}>
              <SelectTrigger className={cn("h-10 w-[120px] shrink-0 rounded-xl bg-white", show("endDate") ? errBorder : okBorder)}>
                <SelectValue placeholder={i18n.t("dateTime.time")} />
              </SelectTrigger>
              <SelectContent className="max-h-[240px]">
                {TIME_OPTIONS.map((t) =>
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          {show("endDate") && <p className="text-[11px] text-[var(--color-error)] mt-1">{errors.endDate}</p>}
        </div>
      </div>

      {/* Results visibility. The second option only brings the first one
          forward, so it stays disabled until results are shared at all;
          unsharing clears it rather than leaving a setting nothing acts on. */}
      <div>
        <label className={fieldLabel}><Trans i18nKey="polls.results" /></label>
        <p className="text-[11px] text-[var(--color-text-secondary)] mb-2">
          You and HR can always see results. Choose when the audience can — leave both
          unchecked to keep results private.
        </p>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.shareResults}
            onChange={(e) =>
              set({
                shareResults: e.target.checked,
                resultsAfterVoting: e.target.checked && form.resultsAfterVoting,
              })
            }
            className="mt-0.5 h-4 w-4" />

          <span>
            <span className="block text-sm font-medium text-foreground">Share results after the poll closes</span>
            <span className="block text-[11px] text-[var(--color-text-secondary)] mt-0.5">
              The whole audience can see results once voting ends.
            </span>
          </span>
        </label>

        <label
          className={cn(
            "flex items-start gap-3 mt-3",
            form.shareResults ? "cursor-pointer" : "cursor-not-allowed opacity-50"
          )}>

          <input
            type="checkbox"
            checked={form.resultsAfterVoting}
            disabled={!form.shareResults}
            onChange={(e) => set({ resultsAfterVoting: e.target.checked })}
            className="mt-0.5 h-4 w-4 disabled:cursor-not-allowed" />

          <span>
            <span className="block text-sm font-medium text-foreground"><Trans i18nKey="polls.showThemRightAfterVoting" /></span>
            <span className="block text-[11px] text-[var(--color-text-secondary)] mt-0.5">
              Each person sees results as soon as they cast their vote.
            </span>
          </span>
        </label>
      </div>
    </div>);

}
